import { useState, useMemo, useCallback, useEffect } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer, type View, type EventProps } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addMonths, startOfMonth, endOfMonth, addDays, subDays, startOfDay, endOfDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import { Button } from '@/components/ui/button';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { DayPicker } from 'react-day-picker';
import { EventDialog } from '@/components/calendar/EventDialog';
import { useCalendarEvents, useCalendarCalendars, useAutoSyncCalendar, useEnsureLocalCalendar } from '@/hooks/useCalendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { 'en-US': enUS },
});

export function CalendarPage() {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [view, setView] = useState<View>('week');
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(undefined);

  // Compute date range based on current view
  const dateRange = useMemo(() => {
    // Extend range to cover view boundaries + buffer
    const start = subDays(startOfMonth(selectedDate), 7).toISOString();
    const end = addDays(endOfMonth(addMonths(selectedDate, 1)), 7).toISOString();
    return { start, end };
  }, [selectedDate]);

  const { data: events = [], isLoading: eventsLoading } = useCalendarEvents(dateRange);
  const { data: calendars = [] } = useCalendarCalendars();

  // Auto-sync with providers on page load and every 5 minutes
  useAutoSyncCalendar();

  // Ensure the local "Buildit" calendar exists for this workspace
  const ensureLocalCalendar = useEnsureLocalCalendar();
  useEffect(() => {
    void ensureLocalCalendar();
  }, [ensureLocalCalendar]);

  // Map DB events to react-big-calendar format
  const calendarEvents = useMemo(() => {
    return events.map(event => ({
      id: event.id,
      title: event.title,
      start: new Date(event.start_time),
      end: event.end_time ? new Date(event.end_time) : new Date(event.start_time),
      allDay: event.all_day,
      resource: {
        calendarId: event.calendar_id,
        color: event.calendar?.color,
        calendarName: event.calendar?.name,
        location: event.location,
        description: event.description,
      },
    }));
  }, [events]);

  const nextMonth = addMonths(selectedDate, 1);

  const handleDateChange = (date: Date | undefined) => {
    if (date) setSelectedDate(date);
  };

  const handleSelectEvent = useCallback((event: any) => {
    // Find the DB event for editing
    const dbEvent = events.find(e => e.id === event.id);
    if (dbEvent) {
      setSelectedEvent({
        id: dbEvent.id,
        title: dbEvent.title,
        description: dbEvent.description,
        start: new Date(dbEvent.start_time),
        end: dbEvent.end_time ? new Date(dbEvent.end_time) : undefined,
        location: dbEvent.location,
        calendar_id: dbEvent.calendar_id,
        all_day: dbEvent.all_day,
      });
      setShowEventDialog(true);
    }
  }, [events]);

  const handleSelectSlot = useCallback(({ start }: { start: Date }) => {
    setSelectedEvent(undefined);
    setSelectedDate(start);
    setShowEventDialog(true);
  }, []);

  // Scroll to 1 hour before the first event today, or 8 AM if no events
  const scrollToTime = useMemo(() => {
    const todayStart = startOfDay(selectedDate);
    const todayEnd = endOfDay(selectedDate);
    const todayEvents = calendarEvents.filter(
      e => !e.allDay && e.start >= todayStart && e.start <= todayEnd
    );

    if (todayEvents.length > 0) {
      const earliest = todayEvents.reduce((min, e) => e.start < min ? e.start : min, todayEvents[0].start);
      const scrollTo = new Date(earliest);
      scrollTo.setHours(scrollTo.getHours() - 1);
      return scrollTo;
    }

    // Default: 8 AM
    const fallback = new Date(selectedDate);
    fallback.setHours(8, 0, 0, 0);
    return fallback;
  }, [calendarEvents, selectedDate]);

  // Custom event component: title only (time is shown via rbc-event-label CSS below)
  const EventComponent = useCallback(({ event }: EventProps) => {
    return (
      <div className="overflow-hidden leading-tight text-xs">
        <span className="font-medium">{event.title}</span>
      </div>
    );
  }, []);

  // Color-code events by calendar
  const eventPropGetter = useCallback((event: any) => {
    const color = event.resource?.color;
    if (color) {
      return {
        style: {
          backgroundColor: color,
          borderColor: color,
          opacity: 0.9,
        },
      };
    }
    return {};
  }, []);

  const calendarClassNames = "w-full [&_.rdp-caption]:hidden [&_.rdp-head]:text-xs [&_.rdp-head_th]:font-normal [&_.rdp-head_th]:text-muted-foreground [&_.rdp-day]:text-sm [&_.rdp-day_button]:h-8 [&_.rdp-day_button]:w-8 [&_.rdp-day_button:hover]:bg-accent [&_.rdp-day_button:focus]:bg-accent [&_.rdp-day_button:focus]:ring-0 [&_.rdp-day_button.rdp-day_today]:bg-accent/50 [&_.rdp-day_button.rdp-day_today]:font-normal [&_.rdp-day_button.rdp-day_selected]:bg-primary [&_.rdp-day_button.rdp-day_selected]:text-primary-foreground [&_.rdp-day_button.rdp-day_selected]:font-normal [&_.rdp-day_button.rdp-day_outside]:opacity-50 [&_.rdp-day_button.rdp-day_outside]:text-muted-foreground/50 [&_.rdp-day_button.rdp-day_outside:hover]:bg-accent/50 [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_.rdp-row]:justify-between [&_.rdp-row]:w-full [&_.rdp-cell]:mx-auto";

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Left Sidebar */}
      <div className="w-72 border-r bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col h-full p-6 space-y-6">
          {/* Current Month Mini Calendar */}
          <Card className="p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">
                {format(selectedDate, 'MMMM yyyy')}
              </h3>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleDateChange(addMonths(selectedDate, -1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => handleDateChange(addMonths(selectedDate, 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleDateChange}
              month={selectedDate}
              showOutsideDays
              className={calendarClassNames}
            />
          </Card>

          {/* Next Month Mini Calendar */}
          <Card className="p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">
                {format(nextMonth, 'MMMM yyyy')}
              </h3>
            </div>
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleDateChange}
              month={nextMonth}
              showOutsideDays
              className={calendarClassNames}
            />
          </Card>

          {/* Calendar list */}
          {calendars.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">My Calendars</h3>
              {calendars.map(cal => (
                <label
                  key={cal.id}
                  className="flex items-center gap-2 text-sm cursor-pointer"
                >
                  <div
                    className="h-3 w-3 rounded-sm"
                    style={{
                      backgroundColor: cal.is_selected ? (cal.color || '#6b7280') : 'transparent',
                      border: `2px solid ${cal.color || '#6b7280'}`,
                    }}
                  />
                  <span className={cal.is_selected ? '' : 'text-muted-foreground'}>
                    {cal.name}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Calendar Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between p-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Calendar</h1>
            <p className="text-sm text-muted-foreground">
              {eventsLoading ? 'Loading events...' : `${calendarEvents.length} event${calendarEvents.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <Button onClick={() => {
            setSelectedEvent(undefined);
            setShowEventDialog(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          <div className="h-[calc(100vh-12rem)]">
            <BigCalendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              defaultView="week"
              view={view}
              onView={setView}
              views={['week', 'month', 'day']}
              className="rounded-lg shadow-sm bg-background [&_.rbc-event-label]:text-[0.65rem] [&_.rbc-event-label]:opacity-70 [&_.rbc-event-label]:leading-tight"
              dayLayoutAlgorithm="no-overlap"
              tooltipAccessor={null}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              date={selectedDate}
              onNavigate={date => setSelectedDate(date)}
              eventPropGetter={eventPropGetter}
              scrollToTime={scrollToTime}
              components={{ event: EventComponent }}
              style={{ height: '100%' }}
            />
          </div>
        </div>
      </div>

      <EventDialog
        open={showEventDialog}
        onOpenChange={setShowEventDialog}
        event={selectedEvent}
        defaultStart={selectedDate}
      />
    </div>
  );
}
